import { Box, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';

interface SendingReminderTimelineProps {
  which: 1 | 2;
  // Föregående utskick: huvudutskicket för Påminnelse 1, Påminnelse 1 för Påminnelse 2.
  previousLabel: string;
  previousSendDate: Dayjs | null;
  previousPayDate: Dayjs | null;
  // Påminnelsens valda datum (null tills kunden valt).
  sendDate: Dayjs | null;
  payDate: Dayjs | null;
}

const fmt = (d: Dayjs | null): string => (d ? d.format('D MMM') : '–');

const daysBetween = (from: Dayjs | null, to: Dayjs | null): number | null => (from && to ? to.startOf('day').diff(from.startOf('day'), 'day') : null);

// En hållpunkt på tidslinjen. Ofyllda punkter (påminnelsen innan datum valts) ritas streckade.
const Point = ({ label, date, active, pending }: { label: string; date: Dayjs | null; active?: boolean; pending?: boolean }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 84 }}>
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid',
        borderStyle: pending ? 'dashed' : 'solid',
        borderColor: active ? 'primary.main' : 'grey.500',
        bgcolor: (() => {
          if (pending) return 'transparent';
          return active ? 'primary.main' : 'grey.500';
        })(),
      }}
    />
    <Typography variant='caption' sx={{ fontWeight: 700, color: active ? 'primary.main' : 'text.secondary', textAlign: 'center' }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 700, lineHeight: 1.2, color: pending ? 'text.disabled' : 'text.primary' }}>
      {fmt(date)}
    </Typography>
  </Box>
);

// Sträckan mellan två hållpunkter, med antal dagar utsatt när båda datumen är kända.
const Leg = ({ days }: { days: number | null }) => (
  <Box sx={{ flex: 1, minWidth: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, pt: 0.25 }}>
    <Box sx={{ width: '100%', borderTop: '2px dashed', borderColor: 'grey.400' }} />
    {days != null && (
      <Typography variant='caption' color='text.secondary'>
        {days} {days === 1 ? 'dag' : 'dagar'}
      </Typography>
    )}
  </Box>
);

/**
 * Tidslinje för en påminnelse: föregående utskick och dess förfallodatum, följt av påminnelsens utskicks- och
 * förfallodatum. Gör det uppenbart hur långt efter förfallodagen påminnelsen landar.
 */
export const SendingReminderTimeline = ({
  which,
  previousLabel,
  previousSendDate,
  previousPayDate,
  sendDate,
  payDate,
}: SendingReminderTimelineProps) => (
  <Box sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Point label={previousLabel} date={previousSendDate} />
      <Leg days={daysBetween(previousSendDate, previousPayDate)} />
      <Point label='Förfaller' date={previousPayDate} />
      <Leg days={daysBetween(previousPayDate, sendDate)} />
      <Point label={`Påminnelse ${which}`} date={sendDate} active={!!sendDate} pending={!sendDate} />
      <Leg days={daysBetween(sendDate, payDate)} />
      <Point label='Förfaller' date={payDate} active={!!payDate} pending={!payDate} />
    </Box>
  </Box>
);
